import React, { useState, useCallback, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import useAppleAnimation from '../hooks/useAppleAnimation';

// Chart.js 컴포넌트 등록
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TaxCalculator = () => {
  // 애니메이션 상태 관리
  const [showContent, setShowContent] = useState(false);
  const [isChartVisible, setIsChartVisible] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // 전체 콘텐츠 애니메이션
  useEffect(() => {
    // 모든 콘텐츠를 한번에 보여주는 애니메이션
    const contentTimer = setTimeout(() => setShowContent(true), 300);

    return () => {
      clearTimeout(contentTimer);
    };
  }, []);

  // 뷰포트 높이가 짧을 때는 애니메이션 트리거 포인트를 조정
  const getAnimationMargin = () => {
    const viewportHeight = window.innerHeight;
    // 뷰포트 높이가 700px 이하면 즉시 표시
    if (viewportHeight <= 700) {
      return '0px 0px 100% 0px'; // 요소가 화면에 나타나자마자 애니메이션
    }
    // 뷰포트 높이가 900px 이하면 트리거 포인트를 높임
    if (viewportHeight <= 900) {
      return '0px 0px -50px 0px';
    }
    // 기본값
    return '0px 0px -200px 0px';
  };

  // 단계별 상태 관리
  const [step, setStep] = useState(1); // 1: 소득정보 입력, 2: 투자금액 입력, 3: 결과 표시

  // 애니메이션 refs - useEffect보다 먼저 선언
  const fadeInRef = useAppleAnimation('apple-hero-slide', 0.1, getAnimationMargin());
  const calculatorFadeInRef = useAppleAnimation('apple-slide-up', 0.1, getAnimationMargin());
  const chartFadeInRef = useAppleAnimation('apple-slide-up', 0.1, '0px 0px -50px 0px');

  // 차트 Intersection Observer - refs 선언 후에 위치
  useEffect(() => {
    if (step === 3 && chartFadeInRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.8) {
              // 차트가 80% 이상 보이면 바 애니메이션 시작
              setTimeout(() => setIsChartVisible(true), 1000); // 차트 영역 애니메이션 완료 후
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.8, // 80% 이상 보일 때 트리거
          rootMargin: '0px'
        }
      );

      observer.observe(chartFadeInRef.current);

      return () => {
        if (chartFadeInRef.current) {
          observer.unobserve(chartFadeInRef.current);
        }
      };
    }
  }, [step]);

  const [comprehensiveIncome, setComprehensiveIncome] = useState(''); // 종합소득금액 (빈 값으로 시작)
  const [taxableIncome, setTaxableIncome] = useState(''); // 작년 과세표준 (빈 값으로 시작)
  const [investment, setInvestment] = useState(''); // 투자금액 (빈 값으로 시작)

  // 종합소득세만 계산 (2025년 기준)
  const calculateIncomeTax = (taxableIncome) => {
    const brackets = [
      { limit: 14000000, rate: 0.06, deduction: 0 },
      { limit: 50000000, rate: 0.15, deduction: 1260000 },
      { limit: 88000000, rate: 0.24, deduction: 5760000 },
      { limit: 150000000, rate: 0.35, deduction: 15440000 },
      { limit: 300000000, rate: 0.38, deduction: 19940000 },
      { limit: 500000000, rate: 0.40, deduction: 25940000 },
      { limit: 1000000000, rate: 0.42, deduction: 35940000 },
      { limit: Infinity, rate: 0.45, deduction: 65940000 }
    ];

    for (const bracket of brackets) {
      if (taxableIncome <= bracket.limit) {
        return Math.max(0, taxableIncome * bracket.rate - bracket.deduction);
      }
    }
    return 0;
  };

  // 벤처투자 후 과세표준 계산
  const calculateNewTaxableIncome = (originalTaxableIncome, investmentAmount, comprehensiveIncomeWon) => {
    const ventureDeduction = calculateVentureDeduction(investmentAmount, comprehensiveIncomeWon);
    return Math.max(0, originalTaxableIncome - ventureDeduction);
  };

  const calculateVentureDeduction = (investment, income) => {
    let deduction = 0;
    if (investment <= 30000000) {
      deduction = investment;
    } else if (investment <= 50000000) {
      deduction = 30000000 + (investment - 30000000) * 0.7;
    } else {
      deduction = 30000000 + 20000000 * 0.7 + (investment - 50000000) * 0.3;
    }
    
    // 과세표준 한도: 종합소득금액에서 기본공제 150만원을 뺀 금액
    const basicDeduction = 1500000; // 기본공제 150만원
    const maxTaxableIncome = Math.max(0, income - basicDeduction);
    
    return Math.min(deduction, income * 0.5, maxTaxableIncome);
  };

  const formatNumber = useCallback((num) => {
    const roundedNum = Math.round(num);
    if (roundedNum >= 100000000) {
      const billions = Math.floor(roundedNum / 100000000);
      const millions = Math.floor((roundedNum % 100000000) / 10000);
      if (millions > 0) {
        return `${billions}억 ${millions.toLocaleString()}만원`;
      }
      return `${billions}억원`;
    }
    if (roundedNum >= 10000) {
      return `${Math.round(roundedNum / 10000).toLocaleString()}만원`;
    }
    return `${roundedNum.toLocaleString()}원`;
  }, []);

  // 투자금액 한도 계산 (종합소득금액의 50% 이내, 최대 1억원)
  const getMaxInvestmentAmount = (comprehensiveIncome) => {
    const income = comprehensiveIncome || 0;
    const fiftyPercentLimit = Math.floor((income * 10000) * 0.5 / 10000); // 만원 단위로 반환
    return Math.min(fiftyPercentLimit, 10000); // 최대 1억원(10000만원) 한도 적용
  };

  // 실시간 계산
  const taxableIncomeWon = (taxableIncome || 0) * 10000;
  const comprehensiveIncomeWon = (comprehensiveIncome || 0) * 10000;
  const investmentWon = (investment || 0) * 10000;
  
  // 작년 세금 계산
  const lastYearIncomeTax = calculateIncomeTax(taxableIncomeWon);
  const lastYearLocalTax = lastYearIncomeTax * 0.1;
  const lastYearTax = lastYearIncomeTax + lastYearLocalTax;
  
  // 벤처투자 후 계산
  const newTaxableIncome = calculateNewTaxableIncome(taxableIncomeWon, investmentWon, comprehensiveIncomeWon);
  const newIncomeTax = calculateIncomeTax(newTaxableIncome);
  const newLocalTax = newIncomeTax * 0.1;
  const newTax = newIncomeTax + newLocalTax;
  const taxSaved = lastYearTax - newTax;

  // 계산기 상단으로 부드럽게 스크롤 (헤더 높이 고려)
  const scrollToCalculator = () => {
    if (calculatorFadeInRef.current) {
      const elementPosition = calculatorFadeInRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100; // 헤더 높이 + 여유 공간
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // 단계별 진행 핸들러
  const handleStep1Complete = () => {
    setStep(2);
    // 투자금액을 종합소득금액의 50% 이내로 조정
    const maxInvestment = getMaxInvestmentAmount(comprehensiveIncome);
    if (investment > maxInvestment) {
      setInvestment(Math.min(maxInvestment, 3000));
    }
    // 단계 변경 후 계산기 상단으로 스크롤
    setTimeout(() => scrollToCalculator(), 100);
  };

  const handleStep2Complete = () => {
    setStep(3);
    // 결과 표시 후 계산기 상단으로 스크롤
    setTimeout(() => scrollToCalculator(), 100);
  };

  // 차트 데이터
  const chartData = {
    labels: ['작년 납부 세금', '벤처투자 후 예상 세금'],
    datasets: [{
      label: '세금',
      data: [Math.round(lastYearTax), Math.round(newTax)],
      backgroundColor: [
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)'
      ],
      borderColor: [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)'
      ],
      borderWidth: 2
    }]
  };

  // 기본 차트 옵션
  const getChartOptions = useCallback(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return formatNumber(context.parsed.y);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatNumber(value);
          }
        }
      }
    }
  }), [isChartVisible, formatNumber]);

  return (
    <section id="calculator" className="py-20 md:pt-16 md:pb-32 bg-gradient-to-b from-gray-50 to-white scroll-mt-[400px] md:scroll-mt-20">
      <div className="container mx-auto px-6">
        <div className={`grid md:grid-cols-2 gap-12 items-start transition-all duration-1000 ease-out ${
          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* 헤로 텍스트 */}
          <div className="text-center md:text-left md:pt-[calc(50vh-20rem)]" ref={fadeInRef}>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 leading-tight mb-4">
              벤처기업투자로 시작하는<br />
              <span className="text-blue-600">가장 스마트한 절세 전략</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              연봉과 투자금액을 입력하고, 지금 바로 예상 절세액을 확인해보세요. 복잡한 세금, 더 이상 고민하지 마세요.
            </p>
          </div>

          {/* 계산기 */}
          <div ref={calculatorFadeInRef} className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
            {/* 진행 단계 표시 */}
            <div className="flex items-center justify-center mb-6">
              {[1, 2, 3].map((stepNum) => (
                <React.Fragment key={stepNum}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-base font-bold ${
                    stepNum <= step ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {stepNum}
                  </div>
                  {stepNum < 3 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      stepNum < step ? 'bg-blue-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* 알림 메시지 */}
            {showAlert && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in-out">
                {alertMessage}
              </div>
            )}

            {/* 1단계: 소득정보 입력 */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">얼마나 절세할 수 있을까요?</h2>
                    <p className="text-gray-600">작년 종합소득세 신고서 기준으로 입력해주세요</p>
                  </div>
                
                {/* 종합소득금액 */}
                <div>
                  <label htmlFor="comprehensiveIncome" className="block text-base font-medium text-gray-700 mb-2">
                    종합소득금액 <span className="text-gray-500">(각종공제 적용 전)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="comprehensiveIncome"
                      min="0"
                      max="150000"
                      value={comprehensiveIncome}
                      placeholder="금액을 입력하세요"
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          setComprehensiveIncome('');
                          return;
                        }
                        const value = Number(inputValue);
                        if (!isNaN(value) && value >= 0) {
                          if (value <= 150000) {
                            setComprehensiveIncome(value);
                          }
                        }
                      }}
                      className="w-full px-4 py-3 pr-16 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg font-semibold"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">
                      만원
                    </span>
                  </div>
                </div>

                {/* 과세표준 */}
                <div>
                  <label htmlFor="taxableIncome" className="block text-base font-medium text-gray-700 mb-2">
                    과세표준 <span className="text-gray-500">(각종공제 적용 후)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="taxableIncome"
                      min="0"
                      max={comprehensiveIncome || 150000}
                      value={taxableIncome === '' ? '' :
                        comprehensiveIncome === '' || comprehensiveIncome === 0 ? taxableIncome :
                        Math.min(taxableIncome, Math.max(0, comprehensiveIncome))}
                      placeholder="금액을 입력하세요"
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          setTaxableIncome('');
                          return;
                        }
                        const value = Number(inputValue);
                        const maxIncome = comprehensiveIncome || 0;
                        if (value > maxIncome) {
                          setAlertMessage('과세표준은 종합소득금액을 초과할 수 없습니다.');
                          setShowAlert(true);
                          setTimeout(() => setShowAlert(false), 3000);
                          setTaxableIncome(maxIncome);
                        } else if (value >= 0) {
                          setTaxableIncome(value);
                        }
                      }}
                      className="w-full px-4 py-3 pr-16 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg font-semibold"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">
                      만원
                    </span>
                  </div>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    <span>최대: {(comprehensiveIncome || 0).toLocaleString()}만원 (종합소득금액 이내)</span>
                  </div>
                </div>


                {/* 투자 가능 한도 미리보기 */}
                <div className="bg-green-50 p-4 rounded-lg text-center border border-green-200">
                  <p className="text-green-800 font-medium mb-1">투자 가능 한도</p>
                  <p className="text-2xl font-bold text-green-600">
                    최대 {getMaxInvestmentAmount(comprehensiveIncome).toLocaleString()}만원
                  </p>
                  <p className="text-green-700 text-base mt-1">
                    (종합소득금액의 50% 이내, 최대 1억원)
                  </p>
                </div>

                {/* 도움말 */}
                <div className="bg-blue-50 p-4 rounded-lg text-base">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"/>
                    </svg>
                    <div>
                      <p className="text-blue-800 font-medium mb-1">종합소득세 신고서에서 확인하세요</p>
                      <p className="text-blue-700">• <strong>종합소득금액</strong>: 근로소득, 사업소득 등의 합계</p>
                      <p className="text-blue-700">• <strong>과세표준</strong>: 종합소득금액에서 각종 공제를 뺀 금액</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl text-center">
                  <p className="text-gray-600 text-lg mb-2">작년에 납부한 소득세</p>
                  <p className="text-3xl font-bold text-red-500">
                    {formatNumber(Math.round(lastYearTax))}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mt-4 text-base">
                    <div>
                      <p className="text-gray-500">종합소득금액</p>
                      <p className="font-bold text-gray-800">{comprehensiveIncome === '' ? '0' : comprehensiveIncome.toLocaleString()}만원</p>
                    </div>
                    <div>
                      <p className="text-gray-500">과세표준</p>
                      <p className="font-bold text-gray-800">{taxableIncome === '' ? '0' : Math.min(taxableIncome, comprehensiveIncome).toLocaleString()}만원</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleStep1Complete}
                  className="w-full bg-blue-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-600 transition-colors mt-8"
                >
                  다음 단계로
                </button>
              </div>
            )}

            {/* 2단계: 투자금액 입력 */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">투자금액을 설정해주세요</h2>
                    <p className="text-gray-600">최대 {getMaxInvestmentAmount(comprehensiveIncome).toLocaleString()}만원까지 투자 가능합니다</p>
                    <p className="text-gray-500 text-base">(종합소득금액의 50% 이내, 최대 1억원)</p>
                  </div>

                  <div>
                  <label htmlFor="investment" className="block text-base font-medium text-gray-700 mb-2">
                    벤처기업 투자금액
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="investment"
                      min="0"
                      max={getMaxInvestmentAmount(comprehensiveIncome)}
                      value={investment === '' ? '' : Math.min(investment, getMaxInvestmentAmount(comprehensiveIncome))}
                      placeholder="금액을 입력하세요"
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          setInvestment('');
                          return;
                        }
                        const value = Number(inputValue);
                        const maxValue = getMaxInvestmentAmount(comprehensiveIncome);
                        if (!isNaN(value) && value >= 0 && value <= maxValue) {
                          setInvestment(value);
                        }
                      }}
                      className="w-full px-4 py-3 pr-16 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg font-semibold"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">
                      만원
                    </span>
                  </div>
                    <div className="text-right text-sm text-gray-500 mt-1">
                      <span>최대: {getMaxInvestmentAmount(comprehensiveIncome).toLocaleString()}만원</span>
                    </div>
                  </div>

                <div className="flex space-x-4 mt-8">
                  <button
                    onClick={() => {
                      setStep(1);
                      setTimeout(() => scrollToCalculator(), 100);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                  >
                    이전 단계
                  </button>
                  <button
                    onClick={handleStep2Complete}
                    className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-blue-600 transition-colors"
                  >
                    <span className="hidden md:inline">절세액 확인하기</span>
                    <span className="md:hidden">절세액<br />확인하기</span>
                  </button>
                </div>
              </div>
            )}

            {/* 3단계: 결과 표시 */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">예상 절세 효과</h2>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl text-center">
                  <p className="text-gray-600 text-lg mb-2">예상 절감 세액</p>
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {formatNumber(Math.round(taxSaved))}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mt-4 text-base">
                    <div>
                      <p className="text-gray-500">작년 납부 세금</p>
                      <p className="font-bold text-gray-800">{formatNumber(Math.round(lastYearTax))}</p>
                      <div className="mt-1 text-base text-gray-400 space-y-0.5 hidden md:block">
                        <p>• 종합소득세: {formatNumber(Math.round(lastYearIncomeTax))}</p>
                        <p>• 지방소득세: {formatNumber(Math.round(lastYearLocalTax))}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-500">투자 후 예상 세금</p>
                      <p className="font-bold text-gray-800">{formatNumber(Math.round(newTax))}</p>
                      <div className="mt-1 text-base text-gray-400 space-y-0.5 hidden md:block">
                        <p>• 종합소득세: {formatNumber(Math.round(newIncomeTax))}</p>
                        <p>• 지방소득세: {formatNumber(Math.round(newLocalTax))}</p>
                      </div>
                    </div>
                  </div>
                </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-600 text-base">투자금액</p>
                      <p className="font-bold text-gray-800">{(investment || 0).toLocaleString()}만원</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-600 text-base">절세 효과</p>
                      <p className="font-bold text-green-600">
                        {investment && investment > 0 ? Math.round(taxSaved / (investment * 10000) * 100) : 0}% 수익률
                      </p>
                    </div>
                </div>

                <button
                  onClick={() => {
                    setStep(1);
                    setTimeout(() => scrollToCalculator(), 100);
                  }}
                  className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-colors mt-8"
                >
                  다시 계산하기
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 스페이서 - 1, 2단계일 때 차트 영역만큼의 공간 확보 */}
        {step !== 3 && (
          <div className="h-[80px] md:h-[100px]" aria-hidden="true"></div>
        )}

        {/* 차트 섹션 - 3단계일 때만 표시 */}
        {step === 3 && (
          <div className="mt-8 bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-gray-100 animate-chartContainer" ref={chartFadeInRef}>
            <h3 className="text-2xl font-bold text-center text-gray-800 mb-6 animate-chartTitle">
              절세 효과 한눈에 보기
            </h3>
            <div className="relative w-full max-w-2xl mx-auto animate-chartArea" style={{ height: '300px' }}>
              <Bar key={isChartVisible ? 'animated' : 'static'} data={chartData} options={getChartOptions()} />
            </div>
            
            {/* 추가 정보 */}
            <div className="mt-6 text-center text-base text-gray-500">
              <p>※ 실제 세금은 개인별 공제 상황에 따라 달라질 수 있습니다</p>
              <p>※ 벤처투자 소득공제는 과세표준의 50% 한도 내에서 적용됩니다</p>
            </div>
          </div>
        )}
      </div>

      {/* 슬라이더 및 애니메이션 스타일 */}
      <style>{`
        @keyframes fade-in-out {
          0% { opacity: 0; transform: translate(-50%, -10px); }
          10% { opacity: 1; transform: translate(-50%, 0); }
          90% { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -10px); }
        }

        .animate-fade-in-out {
          animation: fade-in-out 3s ease-in-out;
        }

        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          background: #3B82F6;
          cursor: pointer;
          border-radius: 50%;
          margin-top: -6px;
        }

        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #3B82F6;
          cursor: pointer;
          border-radius: 50%;
          border: none;
        }

        @media (max-width: 768px) {
          .relative > div {
            height: 250px !important;
          }
        }

        /* 차트 컨테이너 애니메이션 (0~1초) */
        .animate-chartContainer {
          opacity: 0;
          transform: translateY(30px);
          animation: chartContainerUp 1s ease-out forwards;
        }

        .animate-chartTitle {
          opacity: 0;
          transform: translateY(20px);
          animation: chartTitleUp 1s ease-out 0.2s forwards;
        }

        .animate-chartArea {
          opacity: 0;
          transform: translateY(15px);
          animation: chartAreaUp 1s ease-out 0.4s forwards;
        }

        @keyframes chartContainerUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes chartTitleUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes chartAreaUp {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default TaxCalculator;